import React, { useCallback, useState, useMemo, useEffect } from 'react';
import {
  Show,
  Labeled,
  TextField,
  RichTextField,
  useQuery,
  useNotify,
  FunctionField,
  Pagination,
  Responsive,
  SimpleList,
  Datagrid,
  Filter,
  TextInput,
  SelectInput,
  List
} from 'react-admin';
import FormTitleBar from '../../core/components/FormTitleBar';
import { getInitials } from '../../core/utils';
import { withStyles } from '@material-ui/core/styles';
import { Grid, Typography } from '@material-ui/core/';
import moment from 'moment';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Button
} from '@material-ui/core/';
import EmailArchiveAttachmentApi from '../../core/apis/EmailArchiveAttachmentApi';
import GridPopover from '../../core/components/grid/GridPopover';
import { isEmpty } from 'lodash';
import { saveAs } from 'file-saver';
import Empty from '../../core/components/list/Empty';
import {
  predictiveSearchFormatter,
  predictiveSearchParser
} from '../../core/utils';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import DialogTitleWithCloseButton from '../../core/components/DialogTitleWithCloseButton';
import { makeStyles } from '@material-ui/core/styles';
import Accordion from '@material-ui/core/Accordion';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import EmailArchiveExcelDownloadApi from '../../core/apis/EmailArchiveExcelDownloadApi';
import useNotifyServerError from '../../core/hooks/useNotifyServerError';

const useStyles = makeStyles(() => ({
  accordionDetails: {
    display: 'block'
  },
  filterLabel: {
    color: '#757575',
    marginBottom: '-15px'
  },
  root: {
    minWidth: '60%',
    height: '100%'
  },
  fundName: {
    fontSize: 'large',
    fontWeight: 'bold'
  },
  accordionBottomBorder: {
    borderBottom: '1px solid lightgray'
  }
}));

const styles = () => ({
  listPage: {
    margin: '0'
  },
  noActions: {
    marginTop: '0px'
  },
  headerContainer: {
    paddingTop: '0px !important',
    paddingBottom: '12px'
  },
  buttonDownload: {
    fontSize: 14,
    fontWeight: 400,
    color: '#2962FF',
    marginRight: '16px',
    '&:hover': {
      backgroundColor: 'transparent'
    }
  },
  rootContent: {
    paddingTop: '8px',
    paddingLeft: '16px',
    paddingRight: '16px'
  },
  paper: {
    marginTop: '-8px'
  },
  rootItem: {
    marginBottom: '20px',
    ' & > div': {
      '& > div': {
        '& > div': {
          padding: '0'
        }
      }
    }
  },
  rootCell: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    maxWidth: 'none'
  },
  rootRichText: {
    marginBottom: '16px',
    ' & > div': {
      '& > div': {
        '& > div': {
          height: '220px',
          overflow: 'auto',
          '&::-webkit-scrollbar': {
            width: '8px',
            height: '8px'
          },
          /* Track */
          '&::-webkit-scrollbar-track': {
            background: '#ebebeb',
            borderRadius: '10px',
            margin: '2px'
          },
          /* Handle */
          '&::-webkit-scrollbar-thumb': {
            background: '#9E9E9E',
            borderRadius: '10px'
          },
          /* Handle on hover */
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#7b7a7a'
          },
          backgroundColor: 'white',
          border: '1px solid #E0E0E0',
          padding: '0 0 0 7px'
        }
      }
    }
  },
  filtersLabel: {
    fontSize: 15,
    alignSelf: 'center',
    color: '#2962FF',
    paddingRight: '18px',
    '&:hover': {
      cursor: 'pointer'
    }
  },
  root: {
    minWidth: '60%',
    height: '100%'
  },
  buttonSpan: {
    marginBottom: '0px'
  }
});

const EmailArchiveTitle = ({ record, hasExportPermission, filters }) => {
  const [isLoading, setLoading] = useState(false);

  const notify = useNotify();
  /**
   * Click on button 'Download as Excel' to download the excel file
   */
  const handleClickDownloadAsExcel = useCallback(
    id => {
      setLoading(true);
      EmailArchiveExcelDownloadApi.downloadEmailArchiveAsExcel(id, filters)
        .then(resp => {
          const fileName = 'email_content_recipients_filters.xlsx';
          saveAs(resp, fileName);
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
          notify('ra.page.error', { type: 'error' });
        });
    },
    [notify, filters]
  );

  return (
    <FormTitleBar
      name={record.subject}
      showAvatar
      avatarContent={getInitials(record.subject)}
      showButton={hasExportPermission}
      buttonLabel={'Download As Excel'}
      onButtonClick={() => handleClickDownloadAsExcel(record.id)}
      isLoading={isLoading}
      id="download-excel"
    />
  );
};

const Attachments = ({ id: emailArchiveId, classes, ...props }) => {
  const { data, loading } = useQuery({
    type: 'getManyReference',
    resource: 'emailarchiveattachments',
    payload: {
      target: 'emailArchiveId',
      id: emailArchiveId
    }
  });
  const notifyServerError = useNotifyServerError();
  const downloadAttachment = useCallback(
    async attachment => {
      try {
        const response = await EmailArchiveAttachmentApi.downloadAttachmentFile(
          attachment
        );
        saveAs(response.data, attachment.fileName);
      } catch (error) {
        notifyServerError(error);
      }
    },
    [notifyServerError]
  );

  if (loading) return <CircularProgress />;
  if (!data) return null;

  return (
    <Paper>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Attachment</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {Object.values(data).map((attachment, index) => (
            <TableRow key={index}>
              <TableCell classes={{ root: classes.rootCell }}>
                {attachment.fileName}
                <Button
                  onClick={() => downloadAttachment(attachment)}
                  className={classes.buttonDownload}
                >
                  Download
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
};

const emailTermStatus = [
  { id: 'SUCCESS', name: 'Sent' },
  { id: 'INVALID', name: 'Not Sent' }
];

const RecipientsFilter = ({ filterValues, setAppliedFilters, ...rest }) => {
  useEffect(() => {
    if (filterValues) {
      setAppliedFilters(filterValues);
    }
  }, [filterValues, setAppliedFilters]);
  return (
    <Filter variant="standard" {...rest}>
      <TextInput
        label="Search by First Name"
        source="firstName"
        alwaysOn
        parse={predictiveSearchParser}
        format={predictiveSearchFormatter}
        options={{ autoComplete: 'off' }}
      />
      <TextInput
        label="Search by Last Name"
        source="lastName"
        alwaysOn
        parse={predictiveSearchParser}
        format={predictiveSearchFormatter}
        options={{ autoComplete: 'off' }}
      />
      <TextInput
        label="Search by Email"
        source="email"
        alwaysOn
        parse={predictiveSearchParser}
        format={predictiveSearchFormatter}
        options={{ autoComplete: 'off' }}
      />
      <SelectInput
        label="Status"
        source="status"
        choices={emailTermStatus}
        allowEmpty
        alwaysOn
      />
    </Filter>
  );
};

const displayItems = (investor, items, buttonClass) => {
  if (!isEmpty(items)) {
    const itemsArray = Object.values(items);
    return (
      <GridPopover
        text={
          itemsArray.length > 1
            ? investor
              ? 'Multiple Investors'
              : `${itemsArray.length}`
            : itemsArray[0].name
        }
        classes={{ button: buttonClass }}
        multiple={itemsArray.length > 1}
        popoverContent={
          <Paper>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    {investor ? 'Investor Name' : 'Permission Groups Name'}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {itemsArray.map(item => {
                  return (
                    <TableRow key={item.id}>
                      <TableCell>{item.name}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Paper>
        }
      />
    );
  } else {
    return null;
  }
};

const Recipients = ({ id: emailArchiveId, classes, ...props }) => (
  <List
    hasCreate={false}
    hasEdit={false}
    hasList
    hasShow={false}
    resource="emailarchiverecipients"
    basePath="/emailarchive"
    className={classes.listPage}
    sort={{ field: 'userId' }}
    exporter={false}
    title={<></>}
    filters={<RecipientsFilter {...props} />}
    filter={{ emailArchiveId }}
    bulkActionButtons={false}
    perPage={20}
    pagination={<Pagination rowsPerPageOptions={[20, 25, 30]} />}
    actions={null}
    empty={<Empty />}
    syncWithLocation
  >
    <Responsive
      small={
        <SimpleList
          primaryText={record => record.userId}
          secondaryText={displayUserName}
          tertiaryText={() => ''}
        />
      }
      medium={
        <Datagrid>
          <FunctionField
            label="Contact Name"
            render={displayUserName}
            id="contact-name"
          />
          <TextField label="Email" source="email" id={'email'} />
          <TextField label="Fund Name" source="fundName" id={'fund-name'} />
          <FunctionField
            label="Investor Name"
            id={'investor name'}
            render={record =>
              displayItems(true, record.investors, classes.button)
            }
          />
          <FunctionField
            label="Permission Groups Name"
            id={'permission-group'}
            render={record =>
              displayItems(false, record.groups, classes.button)
            }
          />
          <FunctionField label="Status" render={displayStatus} id={'status'} />
        </Datagrid>
      }
    />
  </List>
);

const displayStatus = record =>
  record.status === 'SUCCESS' ? 'Sent' : 'Not sent';

const getFormattedUserName = record =>
  record.userDeleted
    ? `${record.firstName} ${record.lastName} (D)`
    : `${record.firstName} ${record.lastName}`;

const displayUserName = record =>
  record.userId ? getFormattedUserName(record) : '';

/**
 * Get a custom field for rendering text
 */
const CustomTextField = ({
  record,
  label,
  source,
  emptyText = ' ',
  secondSource = ''
}) => {
  if (secondSource !== '') {
    return (
      <CustomFunctionField
        record={record}
        label={label}
        source={source}
        render={record => `${record[source]} ${record[secondSource]}`}
      />
    );
  } else {
    return (
      <div key={source} className={`ra-field ra-field-${source}`}>
        <Labeled label={label} source={source} record={record}>
          <TextField source={source} emptyText={emptyText} />
        </Labeled>
      </div>
    );
  }
};

const CustomFunctionField = ({ record, label, source, render }) => {
  if (render) {
    return (
      <div key={source} className={`ra-field ra-field-${source}`}>
        <Labeled label={label} source={source} record={record}>
          <FunctionField render={render} />
        </Labeled>
      </div>
    );
  }
  return null;
};

const ShowFiltersGrid = ({ record }) => {
  const filters = record.filters;
  const classes = useStyles();

  const handleChange = panel => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };
  const [expanded, setExpanded] = React.useState(false);

  const isAdministrators = includeAdministrator => {
    if (includeAdministrator) {
      return <span>Administrators</span>;
    } else {
      return <div></div>;
    }
  };
  const isInactiveInvestors = inactiveInvestors => {
    if (inactiveInvestors) {
      return <span>Inactive Investors</span>;
    } else {
      return <div></div>;
    }
  };
  const isLoggedInUsers = loggedInUsers => {
    if (loggedInUsers) {
      return <span>Logged in Contacts</span>;
    } else {
      return <div></div>;
    }
  };
  const isUsersWhoHaveNotLoggedIn = usersWhoHaveNotLoggedIn => {
    if (usersWhoHaveNotLoggedIn) {
      return <span>Not Logged in Contacts</span>;
    } else {
      return <div></div>;
    }
  };
  const getExcudedEmailDomains = excludeEmailDomains => {
    if (excludeEmailDomains && excludeEmailDomains.length > 0) {
      const excludeEmailDomainsBody = excludeEmailDomains.map(
        (excludeEmailDomain, i) => {
          return (
            <option key={i} value={excludeEmailDomain}>
              {excludeEmailDomain}
            </option>
          );
        }
      );
      return excludeEmailDomainsBody;
    } else {
      return <Labeled label={'None'} />;
    }
  };
  const getGroupNames = groups => {
    if (groups && groups.length > 0) {
      const groupBody = groups.map((group, i) => {
        return (
          <option key={i} value={group.name}>
            {group.name}
          </option>
        );
      });
      return groupBody;
    } else {
      return <Labeled label={'None'} />;
    }
  };
  const getResourceUploadNames = resourceUpload => {
    if (resourceUpload && resourceUpload.length > 0) {
      const resourceUploadBody = resourceUpload.map((resourceUpload, i) => {
        return (
          <option key={i} value={resourceUpload.name}>
            {resourceUpload.name}
          </option>
        );
      });
      return resourceUploadBody;
    } else {
      return <Labeled label={'None'} />;
    }
  };

  let accordionBody;
  if (filters) {
    accordionBody = filters.map((filter, i) => {
      return (
        <div key={i} className={{ paper: classes.root }}>
          <Accordion
            expanded={expanded === 'panel' + i}
            onChange={handleChange('panel' + i)}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls={'panel' + i + 'a-content'}
              id={'panel' + i + 'a-header'}
              className={classes.accordionBottomBorder}
            >
              <div className={classes.fundName}>{filter.fund.name}</div>
            </AccordionSummary>
            <AccordionDetails className={classes.accordionDetails}>
              <div className={classes.filterLabel}>Selected Contact Types</div>{' '}
              <br />
              <div>{isAdministrators(filter.includeAdministrator)}</div>
              <div>{isInactiveInvestors(filter.includeInactiveInvestors)}</div>
              <div>{isLoggedInUsers(filter.includeLoggedInUsers)}</div>
              <div>
                {isUsersWhoHaveNotLoggedIn(
                  filter.includeUsersWhoHaveNotLoggedIn
                )}
              </div>
              <br />
              <div className={classes.filterLabel}>Excluded Email Domains</div>
              <br />
              <div>{getExcudedEmailDomains(filter.excludeEmailDomains)}</div>
              <br />
              <div className={classes.filterLabel}>
                Selected Permission Groups
              </div>
              <br />
              <div>{getGroupNames(filter.groups)}</div> <br />
              <div className={classes.filterLabel}>Selected Documents</div>{' '}
              <br />
              <div>{getResourceUploadNames(filter.resourceUploads)}</div> <br />
            </AccordionDetails>
          </Accordion>
        </div>
      );
    });
  }

  return <div>{accordionBody}</div>;
};

/**
 * Get User first and last name with checking if the user
 * is deleted in order to add flag: 'D'
 */
const UserNamesTextField = ({ record, label }) => {
  let recipientName =
    record.emailSentFromSystem ||
    (!record.userFirstName && !record.userLastName) ? (
      <div>{'System'}</div>
    ) : record.userDeleted ? (
      <div>
        {record.userFirstName} {record.userLastName} {' (D)'}
      </div>
    ) : (
      <div>
        {record.userFirstName} {record.userLastName}
      </div>
    );
  return (
    <div>
      <Labeled label={label}>{recipientName}</Labeled>
    </div>
  );
};

/**
 * rendering date field in a custom component
 */
const CustomDateField = ({ record, label, source }) => {
  return (
    <div key={source} className={`ra-field ra-field-${source}`}>
      <Labeled label={label} source={source} record={record}>
        <FunctionField
          render={record =>
            moment(record.timestamp).format('MM/DD/YYYY [at] hh:mm A')
          }
        />
      </Labeled>
    </div>
  );
};

/**
 * rendering email body custom
 */
const EmailBodyField = ({ record, label, source }) => (
  <div key={source} className={`ra-field ra-field-${source}`}>
    <Labeled label={label} source={source} record={record}>
      <RichTextField
        source={source}
        onClick={e => {
          if (e.target.href) {
            e.target.target = '_blank';
            e.target.rel = 'noreferrer noopener';
          }
        }}
      />
    </Labeled>
  </div>
);

const AttachmentsField = ({ record, label, source, classes }) => (
  <div key={source}>
    <Labeled label={label} source={source} record={record}>
      <Attachments id={record.id} classes={classes} />
    </Labeled>
  </div>
);

const RecipientsField = ({
  record,
  label,
  source,
  classes,
  setAppliedFilters
}) => (
  <div key={source} className={`ra-field ra-field-${source}`}>
    <Labeled label={label} source={source} record={record}>
      <Recipients
        id={record.id}
        classes={classes}
        setAppliedFilters={setAppliedFilters}
      />
    </Labeled>
  </div>
);

const AdvancedOptionsButton = ({ record, classes, setFiltersDialogOpen }) => {
  if (record.filters && record.filters.length > 0) {
    return (
      <div>
        <Labeled
          label={'Advanced Options'}
          className={classes.buttonSpan}
        ></Labeled>
        <span
          className={classes.filtersLabel}
          onClick={() => setFiltersDialogOpen(true)}
        >
          See Details
        </span>
      </div>
    );
  } else {
    return (
      <div>
        <Labeled
          label={'Advanced Options'}
          className={classes.buttonSpan}
        ></Labeled>
        <span>None</span>
      </div>
    );
  }
};

/**
 * We cant rendering children as we want inside a Labeled component so we need to create custom field for each data to display
 * <Labeled> can only accept one child and doing condition not working like passing a custom props to displaying a field for text, or date or email body
 * {type === "date" && (...)} rendering basically a <TextField>
 * {type === "text" && (...)} rendering basically a <DateField>
 * those two conditions inside the <Labeled> not working.
 */
const EmailArchiveSummary = ({ ...props }) => {
  const { classes, record } = props;

  const [filtersDialogOpen, setFiltersDialogOpen] = useState(false);

  return (
    <Grid container className={classes.rootContent}>
      <Grid item xs={12} className={classes.headerContainer}>
        <Typography variant="h5">Details</Typography>
      </Grid>
      <Grid item xs={12} sm container>
        <Grid item xs={5} className={classes.rootItem}>
          <CustomTextField
            label="Distribution List Name"
            source="listName"
            emptyText="No Distribution list name"
            {...props}
          />
        </Grid>
        <Grid item xs={3} className={classes.rootItem}>
          <CustomTextField
            label="Recipients"
            source="noOfRecipients"
            {...props}
          />
        </Grid>
        <Grid item xs={3} className={classes.rootItem}>
          <CustomDateField label="Date" source="timestamp" {...props} />
        </Grid>
        <Grid item xs={3} className={classes.rootItem}>
          <UserNamesTextField label="Sender" {...props} />
        </Grid>
        <Grid item xs={3} className={classes.rootItem}>
          <AdvancedOptionsButton
            record={record}
            classes={classes}
            setFiltersDialogOpen={setFiltersDialogOpen}
            {...props}
          />
        </Grid>
        <Grid item xs={2} className={classes.rootItem}>
          <CustomTextField
            label="Sent Emails"
            source="nbEmailsSent"
            emptyText="0"
            {...props}
          />
        </Grid>
        <Grid item xs={2} className={classes.rootItem}>
          <CustomTextField
            label="Not Sent Emails"
            source="nbEmailsNotSent"
            emptyText="0"
            {...props}
          />
        </Grid>
        <Grid item xs={2} className={classes.rootItem}>
          <CustomFunctionField
            label="Total Emails"
            source="totalEmails" // fake source
            render={record =>
              `${(record['nbEmailsSent'] || 0) +
                (record['nbEmailsNotSent'] || 0)}`
            }
            {...props}
          />
        </Grid>
      </Grid>
      <Grid item xs={4}>
        {record.noOfAttachments > 0 && (
          <AttachmentsField label="Attachments" {...props} />
        )}
      </Grid>
      <Grid item xs={12} className={classes.rootItem}>
        <CustomTextField label="Subject" source="subject" {...props} />
      </Grid>
      <Grid item xs={12} className={classes.rootRichText}>
        <EmailBodyField source="body" label="Email Body" {...props} />
      </Grid>
      <Grid item xs={12}>
        <RecipientsField label="Recipients" {...props} />
      </Grid>
      <Dialog
        open={filtersDialogOpen}
        aria-label={`Advanced Email Options`}
        maxWidth={'lg'}
        onClose={(event, reason) => {
          if (reason === 'backdropClick') return null;
          setFiltersDialogOpen(false);
        }}
        classes={{ paper: classes.root }}
      >
        <DialogTitleWithCloseButton onClose={() => setFiltersDialogOpen(false)}>
          {'Advanced Email Options'}
        </DialogTitleWithCloseButton>
        <DialogContent>
          <ShowFiltersGrid label="label" {...props} />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setFiltersDialogOpen(false)}
            variant="outlined"
            color="primary"
            label="Close"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

const EmailArchiveShow = ({
  classes,
  hasCreatePermission,
  hasEditPermission,
  hasDeletePermission,
  userProfile,
  ...rest
}) => {
  const { hasExportPermission } = useMemo(() => {
    return {
      hasExportPermission:
        rest.permissions &&
        rest.permissions.includes('AUTH_EXPORT_EMAIL_ARCHIVE')
    };
  }, [rest.permissions]);

  // keep track of applied filters so they can be passed while downloading archive as Excel
  const [appliedFilters, setAppliedFilters] = useState([]);

  return (
    <Show
      {...rest}
      title={
        <EmailArchiveTitle
          hasExportPermission={hasExportPermission}
          filters={appliedFilters}
        />
      }
      classes={{ noActions: classes.noActions }}
    >
      <EmailArchiveSummary
        classes={classes}
        setAppliedFilters={setAppliedFilters}
      />
    </Show>
  );
};

export default withStyles(styles)(EmailArchiveShow);
